import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { FileText, Building2, MapPin, Calendar, Phone } from 'lucide-react';

export function ProformaInvoiceBannerClassic() {
  return (
    <section className="py-12 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
      </div>
      <div className="container mx-auto px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <FileText className="w-10 h-10 text-yellow-400" />
            <span className="bg-yellow-400 text-blue-900 px-4 py-1 rounded-full text-sm font-bold uppercase">
              For Corporate Buyers
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            Need a Proforma Invoice / Quotation?
          </h2>
          <p className="text-blue-100 text-lg mb-6 max-w-2xl mx-auto">
            Get an instant formal quote for your purchase department. Browse products, add to cart, and download a detailed Proforma Invoice with GST breakdown - no payment required!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/products">
              <Button className="bg-yellow-400 hover:bg-yellow-300 text-blue-900 rounded-full uppercase tracking-wider font-bold px-8 py-6 text-base shadow-xl transition-all duration-300 hover:scale-105">
                <FileText className="w-5 h-5 mr-2" />
                Browse & Get Quote
              </Button>
            </Link>
            <div className="text-blue-200 text-sm">
              <span className="block">✓ Instant PDF Download</span>
              <span className="block">✓ GST-Compliant Format</span>
            </div>
          </div>
          <p className="text-blue-300 text-xs mt-4">
            Add products to cart → Click "Download Proforma Invoice" on the cart page
          </p>
        </div>
      </div>
    </section>
  );
}

export function ExperienceCentreClassic() {
  return (
    <section className="py-16 bg-gradient-to-b from-primary/5 to-white">
      <div className="container mx-auto px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Building2 className="w-8 h-8 text-primary" />
              <span className="bg-primary/10 text-primary px-4 py-1 rounded-full text-sm font-bold uppercase">
                Visit Us
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black uppercase mb-4 text-foreground">
              Experience Centre in Mumbai
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See and feel our modular furniture before you buy. Touch the materials, test the quality, and get expert advice.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Map */}
            <div className="rounded-xl overflow-hidden border-2 border-primary/20 shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3769.6694!2d72.8293953!3d19.140858!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDA4JzI3LjEiTiA3MsKwNDknNTUuMSJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="350"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Modular Furniture Store Experience Centre"
              />
            </div>

            {/* Details Card */}
            <div className="bg-white rounded-xl p-8 border border-border shadow-lg">
              <h3 className="font-bold text-xl mb-4 text-foreground flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Modular Furniture Store
              </h3>
              
              <div className="space-y-3 text-muted-foreground mb-6">
                <p>
                  A-611, Crystal Plaza Premises,<br />
                  New Link Road,<br />
                  Andheri (W), Mumbai 400053
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary" />
                  <a href="tel:+919594685818" className="hover:text-primary">9594685818</a> / <a href="tel:+919820042100" className="hover:text-primary">9820042100</a>
                </p>
              </div>

              {/* Appointment Notice */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-amber-800 text-sm">By Appointment Only</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Visits are strictly as per pre-booked appointments.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <a href="tel:+919594685818" className="flex-1">
                  <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full">
                    <Phone className="w-4 h-4 mr-2" />
                    Book Appointment
                  </Button>
                </a>
                <a 
                  href="https://www.google.com/maps/place/19%C2%B008'27.1%22N+72%C2%B049'55.1%22E/@19.140858,72.8293953,17z"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button variant="outline" className="w-full rounded-full">
                    <MapPin className="w-4 h-4 mr-2" />
                    Get Directions
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
